import { useEffect, useMemo, useState, type FormEvent } from 'react';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import {
  useCadastrarContabilidade,
  useClientes,
  useDefinirClientesDaContabilidade,
  useEditarContabilidade,
} from '../../shared/api/queries.ts';
import type { Cliente, Contabilidade } from '../../shared/api/tipos.ts';
import { ApiError } from '../../shared/api/client.ts';
import { CampoEmails } from '../../shared/ui/CampoEmails.tsx';
import { mascararCnpj, somenteDigitos } from '../../shared/format.ts';
import { useSessao } from '../auth/sessao.tsx';
import { AcessosDoContador } from './AcessosDoContador.tsx';

const VAZIO = { razaoSocial: '', cnpj: '', ativa: true, emails: [] as string[] };

/**
 * Cadastro e edição da contabilidade, com o vínculo de clientes junto.
 *
 * Os clientes só aparecem na edição: vincular exige que a contabilidade já
 * exista, e pedir isso antes de salvar obrigaria a tela a inventar uma ordem
 * de operações que o usuário não pediu. O acesso do contador vem depois deles,
 * e na mesma ordem em que se pensa: primeiro quem ele atende, depois a chave
 * com que ele entra.
 */
export function DialogoContabilidade({
  aberto,
  contabilidade,
  aoFechar,
}: {
  aberto: boolean;
  contabilidade?: Contabilidade | undefined;
  aoFechar: () => void;
}) {
  const editando = contabilidade !== undefined;
  const { usuario } = useSessao();
  // Criar login é administração de quem entra no sistema, e a rota `/usuarios`
  // exige administrador — a seção some para os outros em vez de responder 403.
  const souAdministrador = usuario?.papel === 'administrador';

  const [campos, setCampos] = useState(VAZIO);
  const [selecionados, setSelecionados] = useState<Cliente[]>([]);

  const clientes = useClientes();
  const cadastrar = useCadastrarContabilidade();
  const editar = useEditarContabilidade();
  const vincular = useDefinirClientesDaContabilidade();

  const salvando = cadastrar.isPending || editar.isPending || vincular.isPending;
  const erro = cadastrar.error ?? editar.error ?? vincular.error;

  // `clientes.data` é estável entre renders; `?? []` criaria um array novo a
  // cada um, e uma dependência que muda de identidade sempre colocaria o
  // efeito abaixo em laço infinito.
  const listaDeClientes = clientes.data?.itens;
  const opcoes = useMemo(() => listaDeClientes ?? [], [listaDeClientes]);

  useEffect(() => {
    if (!aberto) return;

    setCampos(
      contabilidade === undefined
        ? VAZIO
        : {
            razaoSocial: contabilidade.razaoSocial,
            cnpj: mascararCnpj(contabilidade.cnpj),
            ativa: contabilidade.ativa,
            emails: [...contabilidade.emails],
          },
    );
    setSelecionados(
      contabilidade === undefined
        ? []
        : (listaDeClientes ?? []).filter(
            (cliente) => cliente.contabilidadeId === contabilidade.id,
          ),
    );
    cadastrar.reset();
    editar.reset();
    vincular.reset();
    // Rodar só na abertura, na troca de contabilidade e quando a lista de
    // clientes chega é a intenção: incluir as mutações aqui limparia o erro
    // assim que ele aparecesse.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto, contabilidade, listaDeClientes]);

  const cnpjCompleto = somenteDigitos(campos.cnpj).length === 14;
  const razaoSocialOk = campos.razaoSocial.trim().length >= 3;
  const podeEnviar = cnpjCompleto && razaoSocialOk && !salvando;

  // Quem já é de outra contabilidade será transferido — a tela avisa antes,
  // em vez de a mudança acontecer em silêncio.
  const aTransferir = selecionados.filter(
    (cliente) =>
      cliente.contabilidadeId !== null && cliente.contabilidadeId !== contabilidade?.id,
  );

  function fechar() {
    setCampos(VAZIO);
    setSelecionados([]);
    cadastrar.reset();
    editar.reset();
    vincular.reset();
    aoFechar();
  }

  async function enviar(evento: FormEvent) {
    evento.preventDefault();
    if (!podeEnviar) return;

    const dados = {
      razaoSocial: campos.razaoSocial.trim(),
      cnpj: somenteDigitos(campos.cnpj),
      emails: campos.emails,
    };

    try {
      if (contabilidade === undefined) {
        await cadastrar.mutateAsync(dados);
      } else {
        await editar.mutateAsync({ id: contabilidade.id, ...dados, ativa: campos.ativa });
        await vincular.mutateAsync({
          id: contabilidade.id,
          clienteIds: selecionados.map((cliente) => cliente.id),
        });
      }
      fechar();
    } catch {
      // A mensagem já está em `erro`; o diálogo fica aberto para corrigir.
    }
  }

  return (
    <Dialog
      open={aberto}
      onClose={salvando ? undefined : fechar}
      fullWidth
      maxWidth="sm"
      slotProps={{ paper: { component: 'form', onSubmit: enviar } }}
    >
      <DialogTitle>{editando ? 'Editar contabilidade' : 'Nova contabilidade'}</DialogTitle>

      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {erro !== null && <Alert severity="error">{mensagemDoErro(erro)}</Alert>}

          <TextField
            label="Razão social"
            value={campos.razaoSocial}
            onChange={(evento) =>
              setCampos((atual) => ({
                ...atual,
                razaoSocial: evento.target.value.toUpperCase(),
              }))
            }
            required
            autoFocus
            fullWidth
            slotProps={{ htmlInput: { maxLength: 200, autoCapitalize: 'characters' } }}
          />

          <TextField
            label="CNPJ"
            value={campos.cnpj}
            onChange={(evento) =>
              setCampos((atual) => ({ ...atual, cnpj: mascararCnpj(evento.target.value) }))
            }
            required
            fullWidth
            placeholder="00.000.000/0000-00"
            error={campos.cnpj !== '' && !cnpjCompleto}
            helperText={
              campos.cnpj !== '' && !cnpjCompleto ? 'O CNPJ precisa de 14 dígitos.' : ' '
            }
            slotProps={{
              htmlInput: {
                maxLength: 18,
                inputMode: 'numeric',
                style: { fontVariantNumeric: 'tabular-nums' },
              },
            }}
          />

          <CampoEmails
            emails={campos.emails}
            aoMudar={(emails) => setCampos((atual) => ({ ...atual, emails }))}
          />

          {editando && (
            <>
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={opcoes}
                value={selecionados}
                onChange={(_, novos) => setSelecionados(novos)}
                loading={clientes.isPending}
                getOptionLabel={(cliente) => cliente.razaoSocial}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                noOptionsText="Nenhum cliente cadastrado."
                renderInput={(parametros) => (
                  <TextField
                    {...parametros}
                    label="Clientes vinculados"
                    placeholder="Buscar cliente"
                  />
                )}
                renderOption={(parametros, cliente) => {
                  const { key, ...resto } = parametros;
                  return (
                    <li key={key} {...resto}>
                      <Stack>
                        <Typography variant="body2">{cliente.razaoSocial}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {cliente.cnpjFormatado}
                          {cliente.contabilidadeId !== null &&
                            cliente.contabilidadeId !== contabilidade.id &&
                            ' · já vinculado a outra contabilidade'}
                        </Typography>
                      </Stack>
                    </li>
                  );
                }}
                renderValue={(valores, obterProps) =>
                  valores.map((cliente, indice) => (
                    <Chip
                      size="small"
                      label={cliente.razaoSocial}
                      {...obterProps({ index: indice })}
                      key={cliente.id}
                    />
                  ))
                }
              />

              {aTransferir.length > 0 && (
                <Alert severity="info">
                  {aTransferir.length === 1
                    ? '1 cliente será transferido de outra contabilidade.'
                    : `${aTransferir.length} clientes serão transferidos de outras contabilidades.`}
                </Alert>
              )}

              <div>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={campos.ativa}
                      onChange={(evento) =>
                        setCampos((atual) => ({ ...atual, ativa: evento.target.checked }))
                      }
                    />
                  }
                  label="Contabilidade ativa"
                />
                <FormHelperText sx={{ mt: 0 }}>
                  Desmarcar inativa a contabilidade. Nada é apagado, e o CNPJ segue reservado
                  para ela — e o acesso do contador dela para de entrar.
                </FormHelperText>
              </div>

              {souAdministrador && (
                <AcessosDoContador
                  contabilidadeId={contabilidade.id}
                  razaoSocial={contabilidade.razaoSocial}
                />
              )}
            </>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={fechar} disabled={salvando}>
          Cancelar
        </Button>
        <Button type="submit" variant="contained" disabled={!podeEnviar} loading={salvando}>
          {editando ? 'Salvar' : 'Cadastrar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function mensagemDoErro(erro: unknown): string {
  if (erro instanceof ApiError) {
    return erro.codigo === 'DOMINIO_CONFLITO'
      ? 'Já existe uma contabilidade cadastrada com este CNPJ.'
      : erro.message;
  }
  return erro instanceof Error ? erro.message : 'Não foi possível salvar a contabilidade.';
}
